import React, { useState, useEffect, useRef } from 'react';
import TextTruncate from 'react-text-truncate';

type Highlight = {
  title?: string[];
  'attachments.text'?: string[];
  query: string;
};

type AbstractProps = {
  abstract?: string;
  highlight?: Highlight;
  workId: string | number;
  initialNumAbstractLines: number;
  workUrl: string;
  truncateFullAbstract?: boolean;
  hideAbstract?: boolean;
};

const Abstract: React.FC<AbstractProps> = ({
  abstract,
  highlight,
  workId,
  initialNumAbstractLines,
  workUrl,
  truncateFullAbstract = true,
  hideAbstract = false,
}) => {
  const [numAbstractLines, setNumAbstractLines] = useState(
    initialNumAbstractLines || 3,
  );
  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState(abstract);

  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    const addStrongTag = (text: string, strongTag: string | undefined) => {
      if (strongTag) {
        return text;
      }
      return `<strong>${text}</strong>`;
    };

    const highlightText = () => {
      if (!highlight) return;

      const query = highlight.query;
      const abstractDiv = document.querySelector(
        `#work-abstract-${workId} > div span:nth-child(1)`,
      ) as HTMLElement;

      if (abstractDiv) {
        const currentHtml = abstractDiv.innerHTML;
        const queryRegex = new RegExp(
          `(<strong>)?\\b${query}(\\b|('?s\\b))(?!</strong>)`,
          'ig',
        );

        const newHtml = currentHtml.replace(queryRegex, (text, strongTag) =>
          addStrongTag(text, strongTag),
        );
        if (currentHtml !== newHtml) {
          abstractDiv.innerHTML = newHtml;
        }
      }
    };

    if (abstract && highlight) {
      const query = highlight.query;
      const queryRegex = new RegExp(`\\b${query}(\\b|('?s\\b))`, 'i');

      if (highlight['attachments.text'] && abstract.search(queryRegex) < 0) {
        const highlights = highlight['attachments.text'];
        for (let i = 0; i < highlights.length; i++) {
          const cleanHighlight = highlights[i].replace(/\s+/g, ' ');
          if (
            cleanHighlight.length > 200 &&
            cleanHighlight.search(queryRegex) >= 0
          ) {
            setText(cleanHighlight);
            break;
          }
        }
      }

      const config = { characterData: true, subtree: true, childList: true };
      const abstractDiv = document.querySelector(
        `#work-abstract-${workId}`,
      ) as HTMLElement;

      observerRef.current = new MutationObserver(highlightText);
      observerRef.current.observe(abstractDiv, config);

      return () => {
        observerRef.current?.disconnect();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const extendAbstract = () => {
    const maxNumAbstractLines = truncateFullAbstract ? 20 : 100;
    setNumAbstractLines(maxNumAbstractLines);
    setExpanded(true);
  };

  if (!text) {
    return null;
  }

  let truncateChild;
  if (!expanded) {
    truncateChild = (
      <span onClick={extendAbstract} className="work-card--abstract-read-more">
        {' '}
        ...read more
      </span>
    );
  } else if (truncateFullAbstract) {
    truncateChild = (
      <a href={workUrl} className="work-card--abstract-read-more">
        &hellip;
        <span className="mobile-liswp--expand-button">View full abstract</span>
      </a>
    );
  } else {
    truncateChild = (
      <span className="work-card--abstract-truncated-text">
        &hellip; (abstract truncated)
      </span>
    );
  }

  return (
    <div className="work-card--abstract" id={'work-abstract-' + workId}>
      <TextTruncate
        line={hideAbstract && !expanded ? 1 : numAbstractLines}
        truncateText=""
        text={text}
        textTruncateChild={truncateChild}
      />
    </div>
  );
};

export default Abstract;
